import {Slideshow, SlideshowEvents} from './slideshow'
import {inject, injectable} from 'inversify'
import {ContentPlayerHandler, Synchroniser, TimeListener} from './Synchroniser'
import {SlideData, VoiceoverData} from "@/Core/SlideshowData";
import {PubSub} from "./Core/PubSub";
import {settings, SettingsEvents} from "./Core/Settings";
import {announce} from "@/UI/Announce";

@injectable()
export class Voiceover implements TimeListener {
    private voiceovers: {[key: string]: {audio: HTMLAudioElement, loaded: boolean}} = {};
    private currentVoiceover: VoiceoverFile|null = null;
    private currentAudioStartSeconds = 0;
    private currentAudioEndSeconds = 0;
    private startAudioTimeout: NodeJS.Timeout|null = null;
    private playerHandler: ContentPlayerHandler;
    private announcementsAtEnd: string[] = [];

    public constructor(
        @inject("Slideshow")
        private slideshow: Slideshow,
        @inject("PubSub")
        private pubSub: PubSub,
        @inject("Synchroniser")
        private sync: Synchroniser
    ) {
        this.playerHandler = sync.registerContentPlayer(this);
        this.pubSub.subscribeToEvent(SlideshowEvents.Load, () => this.load());
        this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntering, (slideData) => {
            return this.slideEntering(slideData);
        });

        this.pubSub.subscribeToEvent(SlideshowEvents.SlideEntered, () => {
            return this.slideEntered();
        });

        this.pubSub.subscribeToEvent(SlideshowEvents.SlideExiting, () => {
            this.slideExiting();
        });

        this.pubSub.subscribeToEvent(SettingsEvents.volume, volume => {
            if (this.currentVoiceover)
                this.currentVoiceover.audio.volume = volume;
        });
    }

    load(): Promise<any> {
        let promises = [];
        let voiceovers = this.slideshow.audio;

        for (let voiceover of voiceovers) {
            let audio = new Audio();
            audio.volume = settings.volume;
            this.voiceovers[voiceover.name] = {audio: audio, loaded: false };

            let source = document.createElement('source');
            let foundSource = false;
            for (let f of voiceover.files) {
                if (audio.canPlayType(f.fileType + ";")) {
                    source.type = f.fileType;
                    source.src = f.src;
                    foundSource = true
                }
            }

            if (!foundSource) {
                throw "Your browser does not support MP4 or OGG Vorbis HTML5 audio playback.";
            }

            let promise = new Promise<void>(resolve => {
                console.log("voiceover");
                audio.addEventListener('loadedmetadata', () => {
                    console.log("loaded");
                    let x = this.voiceovers[voiceover.name];
                    x.loaded = true;
                    resolve();
                });
            });

            audio.appendChild(source);
            promises.push(promise);
        }

        this.pubSub.subscribeToEvent(SlideshowEvents.Tick, () => this.voiceoverTimeUpdate());
        this.sync.registerTimeListener(this);

        return Promise.all(promises);
    }

    slideEntering(slide: SlideData) {
        if (this.startAudioTimeout != null)
            clearTimeout(this.startAudioTimeout);
        this.startAudioTimeout = null;

        if (this.currentVoiceover != null) {
            this.currentVoiceover.audio.pause();
        }

        this.currentVoiceover = null;
        this.announcementsAtEnd = [];

        return this.prepareAudio(slide.voiceover);
    }

    slideEntered() {
        return this.startAudio();
    }

    slideExiting() {
        if (this.startAudioTimeout != null)
            clearTimeout(this.startAudioTimeout);
    }

    setPlaybackSpeed(speed: number) {
        if (this.currentVoiceover != null)
            this.currentVoiceover.audio.playbackRate = speed;
    }

    announceAfterCurrentSlideVoiceover(announcement: string) {
        this.announcementsAtEnd.push(announcement);
    }

    private prepareAudio(voiceoverData: VoiceoverData|null|undefined): Promise<void> {
        if (voiceoverData == null) {
            this.currentVoiceover = null;
            return Promise.resolve();
        }

        let voiceover = this.voiceovers[voiceoverData.id];
        if (voiceover == null)
            throw "Could not find audio element for " + voiceoverData.id;

        this.currentVoiceover = voiceover;
        this.setPlaybackSpeed(this.sync.playbackSpeed);
        this.currentVoiceover.audio.volume = settings.volume;
        voiceover.audio.currentTime = voiceoverData.startTime;
        this.currentAudioStartSeconds = voiceoverData.startTime;
        this.currentAudioEndSeconds = voiceoverData.endTime;
        return Promise.resolve();
    }

    private startAudio(): Promise<void> {
        if (this.currentVoiceover == null) {
            this.readAnnouncements();
            return this.playerHandler.finished();
        }

        this.isFinished = false;
        if (this.sync.isPlaying) {
            try {
                let playPromise = this.currentVoiceover.audio.play();
                // IE11 does not return a promise
                if (playPromise === undefined)
                    return Promise.resolve();

                return playPromise.catch(() => {
                    announce("Tutorial paused. To play, please use the button in the navigation region, at the end of the page.");
                    this.sync.stop();
                });
            }
            catch(e) {
                // An exception may be thrown if browser refuses to play the audio file
                announce("Tutorial paused. To play, please use the button in the navigation region, at the end of the page.");
                this.sync.stop();
            }
        }

        return Promise.resolve();
    }

    private isFinished = false;

    private voiceoverTimeUpdate() {
        if (this.currentVoiceover == null || this.isFinished) {
            return;
        }

        let audioTime = this.currentVoiceover.audio.currentTime;
        if (audioTime < this.currentAudioEndSeconds) {
            if (this.sync.isPlaying
                && this.currentVoiceover.audio.paused 
                && this.currentVoiceover.loaded) {
                this.startAudio();
            }
            return;
        }

        this.currentVoiceover.audio.pause();
        this.isFinished = true;
        this.readAnnouncements();
        return this.playerHandler.finished();
    }

    private readAnnouncements() {
        for (let a of this.announcementsAtEnd) {
            announce(a);
        }
    }

    stopping() {
        if (this.currentVoiceover != null) {
            this.currentVoiceover.audio.pause();
            this.currentVoiceover.audio.currentTime = this.currentAudioStartSeconds;
        }
    }

    playing() {
        if (this.slideshow.currentSlide.voiceover)
            return this.startAudio();
        return Promise.resolve();
    }

    pausing() {
        if (this.currentVoiceover != null)
            this.currentVoiceover.audio.pause();
    }

    seek(time: number) {
        if (this.currentVoiceover != null) {
            this.currentVoiceover.audio.currentTime = time + this.currentAudioStartSeconds;
        }
    }

    endTime() {
        return this.currentAudioEndSeconds - this.currentAudioStartSeconds;
    }
}

interface VoiceoverFile {
    audio: HTMLAudioElement;
    loaded: boolean;
}